import { serializeError } from 'serialize-error';
import { FIFTEEN_SECONDS_IN_MS } from '../../../../../constants/common';
import { withTimeout } from '../../../../../helpers/withTimeout';
import type Logger from '../../../../Logger';
import type { LtOptions } from './components/lower-third/lowerThird';
import type { tsvb } from './tsvb';
import type { ErrorObject } from './utils/errorBus';
import type ConfigService from '../../../../../config/ConfigService';
import OperationError from '../../../../../errors/OperationError';

export enum ErrorType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}
export enum ErrorEmitter {
  TSVB = 'tsvb',
  COMPONENTS_SYSTEM = 'components_system',
  SRTEAM_PROCESSOR = 'stream_processor',
  ML_INFERENCE = 'ml_inference',
  PRESET_INIT = 'preset_init',
  RENDERER = 'renderer',
  RECORDER = 'recorder',
  EFFECT_VIRTUAL_BACKGROUND = 'effect_virtual_background',
  EFFECT_COLOR_CORRECTION = 'effect_color_correction',
  EFFECT_COLOR_FILTER = 'effect_color_filter',
  EFFECT_SMART_ZOOM = 'effect_smart_zoom',
  EFFECT_LOW_LIGHT = 'effect_low_light',
}

export type LayoutTypes = 'center' | 'left-bottom' | 'right-bottom';
export type LowerThirdTypes = 'lowerthird_1' | 'lowerthird_2' | 'lowerthird_3' | 'lowerthird_4' | 'lowerthird_5'

const ESDK_INIT_INTERVAL_MS = 100;
const MAX_ALLOWED_FILTERS = 2;

/**
 * Набор визуальных эффектов, который можно применить к локальному видеотреку.
 *
 * Подходит для background blur, замены фона, базовой beautification-обработки
 * и оверлеев вроде lower third.
 *
 * @example
 * ```ts
 * await videoTrack.applyEffects({
 *   blur: 0.6,
 *   beautification: 0.3,
 *   colorFilter: {
 *     url: '/filters/warm.cube',
 *     power: 0.4,
 *   },
 * });
 * ```
 */
export interface Effects {
  /** Сила размытия фона за спиной пользователя. */
  blur?: number;
  /** URL или идентификатор изображения, которое будет использовано как виртуальный фон. */
  background?: string;
  /** Сплошной цвет фона в числовом формате, если нужен однотонный background вместо изображения. */
  backgroundColor?: number;
  /** Интенсивность эффекта улучшения внешности и сглаживания изображения. */
  beautification?: number;
  /** Интенсивность цветокоррекции для более выразительной картинки. */
  colorCorrector?: number;
  /** Сила улучшения изображения при недостаточном освещении. */
  lowLightEffect?: number;
  /** Размер области лица в кадре, который smart zoom старается удерживать в фокусе. */
  smartZoom?: number;
  /** Положение пользователя в кадре при активных background-эффектах. */
  layout?: LayoutTypes;
  /** LUT-фильтр для стилизации цветовой палитры видеопотока. */
  colorFilter?: {
    /** Ссылка на LUT-ресурс фильтра. */
    url: string;
    /** Интенсивность наложения LUT-фильтра. */
    power: number;
  };
  /** Конфигурация нижнего графического баннера поверх видео. */
  lowerThird?: {
    /** Вариант шаблона lower third. */
    type: LowerThirdTypes;
    /** Настройки выбранного шаблона lower third, например тексты и стили. */
    options?: LtOptions;
  };
  /** Интенсивность повышения резкости изображения. */
  sharpness?: number;
}

interface VideoTrackProcessorParams {
  loggerFactory: (namespace: string) => Logger;
  customerId?: string;
  sdk_url?: string;
  api_url?: string;
  config: ConfigService;
  onModelsLoaded?: () => void;
}

declare global {
  interface Window {
    tsvb: typeof tsvb;
  }
}

interface InitSteps {
  lowLighter: boolean;
  colorCorrector: boolean;
  faceDetector: boolean;
  segmentation: boolean;
}

export default class EffectsSDKTrackProcessor {
  readonly #tsvb: tsvb;

  readonly #logger: Logger;

  readonly #initSteps: InitSteps = {
    lowLighter: false,
    colorCorrector: false,
    faceDetector: false,
    segmentation: false,
  };

  #originalTrack: MediaStreamTrack | null = null;

  #effects?: Effects;

  #isInitialized = false;

  #isPipelineReady = false;

  #onModelsLoaded?: () => void;

  #onRuntimeError?: (error: ErrorObject) => void;

  constructor(params: VideoTrackProcessorParams) {
    const { loggerFactory } = params;
    this.#logger = loggerFactory('EffectsSDKTrackProcessor');

    this.#onModelsLoaded = params.onModelsLoaded;

    if (!params.customerId) {
      throw new Error('Customer ID must be provided');
    }

    this.#tsvb = new window.tsvb(params.customerId);

    this.#tsvb.onError(this.tsvbErrorHandler.bind(this));

    this.#tsvb.onReady = this.onReady.bind(this);

    const staticFilesPath = params.config.get('staticFilesPath');
    const { localDir, version } = params.config.get('effectsSDKParams');

    const pathToWasm = localDir && version
      ? `${ staticFilesPath }/esdk/${ version ?? '3.8.0' }`
      : `https://effectssdk.ai/sdk/web/${ version ?? '3.8.0' }`;

    const config = {
      preset: 'balanced',
      provider: 'webgpu',
      sdk_url: params.sdk_url,
      api_url: params.api_url,
      test_inference: true,
      wasmPaths: {
        'ort-wasm.wasm': `${ pathToWasm }/ort-wasm.wasm`,
        'ort-wasm-simd.wasm': `${ pathToWasm }/ort-wasm-simd.wasm`,
        'ort-wasm-simd.jsep.wasm': `${ pathToWasm }/ort-wasm-simd.jsep.wasm`,
      },
    };

    this.#tsvb.config(config);
  }

  @withTimeout(FIFTEEN_SECONDS_IN_MS, 'EffectsSDKTrackProcessor.init')
  async init(): Promise<void> {
    await this.#tsvb.preload();
    await this.#tsvb.cache();
    this.#isInitialized = true;
  }

  @withTimeout(FIFTEEN_SECONDS_IN_MS)
  async awaitReady(): Promise<void> {
    await new Promise<void>((resolve) => {
      const iid = setInterval(() => {
        if (this.isModelsLoaded) {
          clearInterval(iid);
          resolve();
        }
      }, ESDK_INIT_INTERVAL_MS);
    });
  }

  setOnRuntimeError(callback: (error: ErrorObject) => void): void {
    this.#onRuntimeError = callback;
  }

  private tsvbErrorHandler(error: ErrorObject): void {
    const {
      emitter, type, ...rest
    } = error;
    const message = emitter ?? 'N/A emitter';

    if (type === ErrorType.ERROR) {
      this.#logger.error(message, { error: serializeError(error), ...rest });
      this.#onRuntimeError?.(error);
    }

    if (type === ErrorType.WARNING) {
      this.#logger.warn(message, { ...rest });
    }

    if (type === ErrorType.INFO) {
      this.#logger.debug(message, { ...rest });
    }

    if (emitter === ErrorEmitter.ML_INFERENCE) {
      if (rest.message.toLocaleLowerCase().includes('init lowlighter')) {
        this.#initSteps.lowLighter = true;
      }

      if (rest.message.toLocaleLowerCase().includes('init colorcorrector')) {
        this.#initSteps.colorCorrector = true;
      }

      if (rest.message.toLocaleLowerCase().includes('init facedetector')) {
        this.#initSteps.faceDetector = true;
      }

      if (rest.message.toLocaleLowerCase().includes('init segmentation')) {
        this.#initSteps.segmentation = true;
      }

      if (this.isModelsLoaded) {
        this.#onModelsLoaded?.();
      }
    }
  }

  get isModelsLoaded(): boolean {
    return this.#initSteps.colorCorrector
      && this.#initSteps.faceDetector
      && this.#initSteps.lowLighter
      && this.#initSteps.segmentation;
  }

  get modelsLoadProgress(): number {
    const progress = Object.values(this.#initSteps).filter(Boolean).length / Object.keys(this.#initSteps).length;

    return progress;
  }

  get isReady(): boolean {
    return this.#isPipelineReady && this.#isInitialized;
  }

  setOnLoadModels(callback: () => void): void {
    this.#onModelsLoaded = callback;

    if (this.isModelsLoaded) {
      this.#onModelsLoaded();
    }
  }

  onReady(): void {
    this.#isPipelineReady = true;
    this.#logger.debug('onReady()', { description: 'ESDK is ready' });
    this.updateEffects(this.#effects);
  }

  processTrack(track: MediaStreamTrack): MediaStreamTrack {
    if (this.#originalTrack === track || track.readyState === 'ended') {
      return this.#tsvb.getStream()?.getVideoTracks()[0] ?? track;
    }

    this.stopProcessing();

    try {
      this.#tsvb.useStream(new MediaStream([track]));
      const outTrack = this.#tsvb.getStream()?.getVideoTracks()[0];

      if (outTrack) {
        this.#originalTrack = track;

        return outTrack;
      }

      return track;
    } catch (error) {
      this.#logger.error('processTrack()', { error, track });
      throw new OperationError('esdk_process_track_failed', error);
    }
  }

  stopProcessing(): void {
    try {
      this.#tsvb.clear();
      this.#tsvb.stop();
    } catch (error) {
      this.#logger.error('stopProcessing()', { error });
    } finally {
      this.#originalTrack = null;
      this.#isPipelineReady = false;
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  updateEffects(effects?: Effects): void {
    this.#effects = effects;

    if (!this.#isPipelineReady) {
      this.#logger.warn('updateEffects()', { description: 'Esdk pipeline not ready' });

      return;
    }

    if (!this.#effects || Object.keys(this.#effects).length === 0) {
      this.#tsvb.stop();

      return;
    }

    let enabledFiltersCount = 0;

    if (this.#effects?.blur && !this.#effects?.backgroundColor) {
      this.#tsvb.setBlur(this.#effects.blur);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.clearBlur();
    }

    if (this.#effects?.background !== undefined) {
      this.#tsvb.setBackground(this.#effects.background);
      enabledFiltersCount += 1;
    } else if (this.#effects?.backgroundColor !== undefined) {
      this.#tsvb.setBackgroundColor(this.#effects?.backgroundColor);
      this.#tsvb.setBackground('color');
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.clearBackground();
    }

    if (this.#effects?.beautification) {
      this.#tsvb.enableBeautification();
      this.#tsvb.setBeautificationLevel(this.#effects.beautification);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableBeautification();
    }

    if (this.#effects?.lowLightEffect) {
      this.#tsvb.enableLowLightEffect();
      this.#tsvb.setLowLightEffectPower(this.#effects.lowLightEffect);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableLowLightEffect();
    }

    if (this.#effects?.colorCorrector) {
      this.#tsvb.enableColorCorrector();
      this.#tsvb.setColorCorrectorPower(this.#effects.colorCorrector);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableColorCorrector();
    }

    if (this.#effects?.smartZoom) {
      this.#tsvb.enableSmartZoom();
      this.#tsvb.setFaceArea(this.#effects.smartZoom);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableSmartZoom();
    }

    if (this.#effects?.layout) {
      this.#tsvb.setLayout(this.#effects.layout);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.setLayout('center');
    }

    if (this.#effects?.colorFilter
      && this.#effects?.colorFilter.power
      && this.#effects?.colorFilter.url) {
      this.#tsvb.setColorFilterConfig({
        lut: this.#effects.colorFilter.url,
        power: this.#effects.colorFilter.power,
      });
      this.#tsvb.enableColorFilter();
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableColorFilter();
    }

    if (this.#effects?.lowerThird) {
      const lowerThird = this.#tsvb.createComponent({
        component: this.#effects.lowerThird.type,
        options: this.#effects.lowerThird.options ?? {},
      });

      this.#tsvb.components?.lowerThird?.hideLowerThird?.();

      this.#tsvb.addComponent(lowerThird, 'lowerThird');
      this.#tsvb.components.lowerThird.showLowerThird();
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.components?.lowerThird?.hideLowerThird?.();
    }

    if (this.#effects?.sharpness) {
      this.#tsvb.setSharpnessEffectConfig(this.#effects.sharpness);
      enabledFiltersCount += 1;
    } else {
      this.#tsvb.disableSharpnessEffect();
    }

    if (enabledFiltersCount > MAX_ALLOWED_FILTERS) {
      this.#tsvb.enableFrameSkipping();
    } else {
      this.#tsvb.disableFrameSkipping();
    }

    this.#tsvb.run();
  }
}
